import { zodResolver } from '@hookform/resolvers/zod';
import { ExternalResourceType } from 'lib-knowledge-management-common/graphql';
import { useModal } from 'lib-knowledge-modal';
import { trimHexValue } from 'lib-knowledge-utils/colors';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import useCollectionFetch from 'hooks/useCollectionFetch';
import useCollectionUpdate from 'hooks/useCollectionUpdate';

import { CollectionModalBody } from './components/collectionModalBody';

// Define the schema for the form fields
export const collectionModalFormSchema = z.object({
  collectionTitle: z.string().min(3, {
    message: 'Name must be at least 3 characters long',
  }),
  internalKbEnabled: z.boolean(),
  internalOnly: z.boolean(),
  description: z.string(),
  iconColor: z.string(),
  linkedQueues: z.array(
    z.object({
      name: z.string(),
      id: z.string(),
    }),
  ),
});

export type CollectionModalForm = z.infer<typeof collectionModalFormSchema>;
export const initialCollectionModalFormValues: CollectionModalForm = {
  collectionTitle: '',
  internalKbEnabled: false,
  internalOnly: false,
  description: '',
  iconColor: '',
  linkedQueues: [],
};

type EditCollectionModalProps = {
  collectionId: string;
  isSyncedWithElevio: boolean;
};

export function CollectionEditModal({
  collectionId,
  isSyncedWithElevio,
}: EditCollectionModalProps): JSX.Element {
  const { hideModal } = useModal();
  const {
    control,
    formState: { isValid, isDirty },
    handleSubmit,
    reset,
    setValue,
  } = useForm<CollectionModalForm>({
    resolver: zodResolver(collectionModalFormSchema),
    defaultValues: initialCollectionModalFormValues,
    reValidateMode: 'onChange',
    mode: 'all',
  });

  const { successNotify, errorNotify } = useNotification();

  const { data: collectionData, loading: collectionLoading } =
    useCollectionFetch({
      collectionId,
    });

  // Insert incoming data into form
  useEffect(() => {
    if (!collectionData || !collectionData.collection) return;

    reset({
      collectionTitle: collectionData.collection.title,
      internalKbEnabled: collectionData.collection.internalKbEnabled,
      internalOnly: collectionData.collection.internalOnly,
      description: collectionData.collection.description ?? '',
      iconColor: trimHexValue(collectionData.collection.themeColors.icon),
      linkedQueues: collectionData.collection.linkedResources.map(
        mapQueueTypeToResource,
      ),
    });
  }, [collectionData, reset]);

  const [update, updateResult] = useCollectionUpdate({
    collectionId,
  });

  const loading = updateResult.loading || collectionLoading;

  async function updateCollection(values: CollectionModalForm): Promise<void> {
    if (!collectionData?.collection) return;

    try {
      await update({
        title: values.collectionTitle,
        internalKbEnabled: values.internalKbEnabled,
        internalOnly: values.internalOnly,
        initialResourceInputs: collectionData.collection.linkedResources.map(
          mapQueueTypeToResource,
        ),
        currentResourceInputs: values.linkedQueues.map(mapQueueTypeToResource),
        icon: values.iconColor,
        description: values.description,
      });
      hideModal();
      successNotify({
        title: 'Collection updated',
        text: `"${values.collectionTitle}" has been updated`,
      });
    } catch (_error) {
      errorNotify({
        title: 'Unable to update collection',
        text: `Please try again. If this issue persists, please contact customer support.`,
      });
    }
  }

  return (
    <CollectionModalBody
      title="Edit collection"
      collectionId={collectionId}
      isSyncedWithElevio={isSyncedWithElevio}
      control={control}
      setValue={setValue}
      linkedHelpCenters={collectionData?.collection?.linkedHelpCenters}
      disabled={loading}
      loading={loading}
      isValid={isValid && isDirty}
      closeModal={hideModal}
      onSave={handleSubmit(updateCollection)}
    />
  );
}

function mapQueueTypeToResource(r: { id: string; name: string }) {
  return {
    __typename: 'Queue',
    type: ExternalResourceType.Queue,
    id: r.id,
    name: r.name,
  };
}
